import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import {
  pricingVariantsApi,
  PricingVariant,
  ExperimentAnalytics,
} from '../../../api/pricingVariants';
import { useConfirm } from '../../../components/shared/ConfirmDialog';
import { CloneFormData, UseExperimentReturn, VariantFormData } from '../types';

const emptyVariantForm: VariantFormData = {
  name: '',
  description: '',
  weight: 0,
  is_control: false,
};

const emptyCloneForm: CloneFormData = {
  code_suffix: '',
  price_multiplier: 1,
};

export function useExperiment(): UseExperimentReturn {
  const confirm = useConfirm();

  const [variants, setVariants] = useState<PricingVariant[]>([]);
  const [analytics, setAnalytics] = useState<ExperimentAnalytics | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [isVariantDialogOpen, setIsVariantDialogOpen] = useState(false);
  const [editingVariant, setEditingVariant] = useState<PricingVariant | null>(null);
  const [variantForm, setVariantForm] = useState<VariantFormData>(emptyVariantForm);

  const [isCloneDialogOpen, setIsCloneDialogOpen] = useState(false);
  const [cloningVariant, setCloningVariant] = useState<PricingVariant | null>(null);
  const [cloneForm, setCloneForm] = useState<CloneFormData>(emptyCloneForm);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    try {
      const [variantsData, analyticsData] = await Promise.all([
        pricingVariantsApi.getAll(),
        pricingVariantsApi.getAnalytics(),
      ]);
      setVariants(variantsData);
      setAnalytics(analyticsData);
    } catch (error: any) {
      toast.error(
        error.response?.data?.message || 'Не удалось загрузить ценовые варианты',
      );
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openCreateVariant = () => {
    setEditingVariant(null);
    setVariantForm(emptyVariantForm);
    setIsVariantDialogOpen(true);
  };

  const openEditVariant = (variant: PricingVariant) => {
    setEditingVariant(variant);
    setVariantForm({
      name: variant.name,
      description: variant.description ?? '',
      weight: variant.weight,
      is_control: variant.is_control,
    });
    setIsVariantDialogOpen(true);
  };

  const submitVariant = async () => {
    if (isSubmitting) return;
    if (!variantForm.name.trim()) {
      toast.error('Укажите название варианта');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload = {
        name: variantForm.name.trim(),
        description: variantForm.description.trim(),
        weight: variantForm.weight,
        is_control: variantForm.is_control,
      };

      if (editingVariant) {
        await pricingVariantsApi.update(editingVariant.id, payload);
        toast.success('Вариант обновлён');
      } else {
        await pricingVariantsApi.create(payload);
        toast.success('Вариант создан');
      }

      setIsVariantDialogOpen(false);
      setEditingVariant(null);
      await refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось сохранить вариант');
    } finally {
      setIsSubmitting(false);
    }
  };

  const openClonePlans = (variant: PricingVariant) => {
    setCloningVariant(variant);
    setCloneForm({ ...emptyCloneForm });
    setIsCloneDialogOpen(true);
  };

  const submitClone = async () => {
    if (!cloningVariant || isSubmitting) return;
    if (!cloneForm.code_suffix.trim()) {
      toast.error('Укажите суффикс кода: коды тарифов уникальны на всю базу');
      return;
    }

    setIsSubmitting(true);
    try {
      const created = await pricingVariantsApi.clonePlans(cloningVariant.id, {
        code_suffix: cloneForm.code_suffix.trim().toUpperCase(),
        price_multiplier: cloneForm.price_multiplier,
        from_variant_id: cloneForm.from_variant_id || undefined,
      });
      toast.success(`Скопировано тарифов: ${created.length}`);
      setIsCloneDialogOpen(false);
      setCloningVariant(null);
      await refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось скопировать тарифы');
    } finally {
      setIsSubmitting(false);
    }
  };

  const activate = async (variant: PricingVariant) => {
    const ok = await confirm({
      title: `Запустить набор в «${variant.name}»?`,
      description:
        'С этого момента новым пользователям будут выдаваться назначения. ' +
        'Первый запущенный вариант задаёт дату старта теста: в набор попадают ' +
        'только те, кто зарегистрировался после неё.',
      confirmLabel: 'Запустить',
    });
    if (!ok) return;

    try {
      await pricingVariantsApi.activate(variant.id);
      toast.success('Набор запущен');
      await refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось запустить вариант');
    }
  };

  const stopEnrollment = async (variant: PricingVariant) => {
    const ok = await confirm({
      title: `Остановить набор в «${variant.name}»?`,
      description:
        'Новых назначать перестанем, уже назначенные продолжат видеть свои цены ' +
        'и останутся в сравнении. Это обратимо: вариант можно запустить снова.',
      confirmLabel: 'Остановить набор',
    });
    if (!ok) return;

    try {
      await pricingVariantsApi.stopEnrollment(variant.id);
      toast.success('Набор остановлен');
      await refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось остановить набор');
    }
  };

  const archive = async (variant: PricingVariant) => {
    const control = variants.find((v) => v.is_control && v.id !== variant.id);

    const ok = await confirm({
      title: `Архивировать «${variant.name}»?`,
      description:
        `Все назначенные будут переведены на «${control?.name ?? 'контрольный вариант'}» ` +
        'и исключены из сравнения: они видели одни цены, а купят по другим. ' +
        'Действие необратимо. Если нужно просто прекратить набор — используйте «Стоп набора».',
      confirmLabel: 'Архивировать',
      destructive: true,
    });
    if (!ok) return;

    try {
      await pricingVariantsApi.archive(variant.id);
      toast.success('Вариант заархивирован, назначенные переведены');
      await refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось заархивировать вариант');
    }
  };

  const remove = async (variant: PricingVariant) => {
    const ok = await confirm({
      title: `Удалить «${variant.name}»?`,
      description:
        'Удалить можно только вариант, который ни разу не работал: без назначений ' +
        'и без заказов по его тарифам. Тарифы варианта будут удалены вместе с ним.',
      confirmLabel: 'Удалить',
      destructive: true,
    });
    if (!ok) return;

    try {
      await pricingVariantsApi.delete(variant.id);
      toast.success('Вариант удалён');
      await refresh();
    } catch (error: any) {
      toast.error(error.response?.data?.message || 'Не удалось удалить вариант');
    }
  };

  return {
    variants,
    analytics,
    isLoading,
    isSubmitting,

    isVariantDialogOpen,
    editingVariant,
    variantForm,
    setVariantForm,
    openCreateVariant,
    openEditVariant,
    setIsVariantDialogOpen,
    submitVariant,

    isCloneDialogOpen,
    cloningVariant,
    cloneForm,
    setCloneForm,
    openClonePlans,
    setIsCloneDialogOpen,
    submitClone,

    activate,
    stopEnrollment,
    archive,
    remove,
    refresh,
  };
}
